import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    makeStyles,
    Option,
    Spinner,
    tokens,
    type TableColumnDefinition,
} from '@fluentui/react-components';
import { Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import type { HeadOfInstituteItem } from '../../models/HeadOfInstituteItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
    },
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    emptyStateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: tokens.colorNeutralForeground3,
    },
    drawerContent: {
        padding: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    formLabel: {
        fontWeight: 600,
        fontSize: '14px',
        color: tokens.colorNeutralForeground1,
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalL,
    },
    yesNoTag: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        display: 'inline-block',
    },
});

const HeadOfInstitutesTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<HeadOfInstituteItem[]>([]);
    const [mitarbeiter, setMitarbeiter] = useState<ProjektmitarbeiterItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<HeadOfInstituteItem | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<HeadOfInstituteItem>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [approvalStep1SearchResults, setApprovalStep1SearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [approvalStep2SearchResults, setApprovalStep2SearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [approvalStep1InputValue, setApprovalStep1InputValue] = useState<string>('');
    const [approvalStep2InputValue, setApprovalStep2InputValue] = useState<string>('');

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch HeadOfInstitute list
    const fetchHeadsOfInstitute = React.useCallback(async () => {
        if (!dataService) return;
        try {
            const headsOfInstitute = await dataService.getHeadsOfInstitute();
            setItems(headsOfInstitute || []);
        } catch (err) {
            console.error('Error fetching heads of institute:', err);
        }
    }, [dataService]);

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            if (!dataService) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const [mitarbeiters, headsOfInstitute] = await Promise.all([
                    dataService.getMitarbeiter(),
                    dataService.getHeadsOfInstitute(),
                ]);
                setMitarbeiter(mitarbeiters || []);
                setItems(headsOfInstitute || []);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [dataService]);

    // Get mitarbeiter name by ID
    const getMitarbeiterName = (userId: number | undefined): string => {
        if (!userId) return '-';
        const person = mitarbeiter.find((m) => m.personalID === userId);
        return person ? (person.fullName || person.text || '-') : '-';
    };

    // Handle search for Approval Step 1
    const handleApprovalStep1Search = (value: string) => {
        if (value.length < 2) {
            setApprovalStep1SearchResults([]);
            return;
        }
        const filtered = mitarbeiter.filter((m) => {
            const fullName = m.fullName || `${m.vorname} ${m.nachname}`;
            return fullName.toLowerCase().includes(value.toLowerCase());
        });
        setApprovalStep1SearchResults(filtered);
    };

    // Handle search for Approval Step 2
    const handleApprovalStep2Search = (value: string) => {
        if (value.length < 2) {
            setApprovalStep2SearchResults([]);
            return;
        }
        const filtered = mitarbeiter.filter((m) => {
            const fullName = m.fullName || `${m.vorname} ${m.nachname}`;
            return fullName.toLowerCase().includes(value.toLowerCase());
        });
        setApprovalStep2SearchResults(filtered);
    };

    // Handle edit drawer open
    const handleEditItem = (item: HeadOfInstituteItem) => {
        setEditingItem(item);
        setEditFormData({ ...item });
        setIsEditDrawerOpen(true);
        setSaveError(null);

        // Pre-populate input values and search results with currently selected mitarbeiter
        const currentStep1 = mitarbeiter.find((m) => m.personalID === item.userFreigabe1);
        if (currentStep1) {
            setApprovalStep1InputValue(currentStep1.fullName || `${currentStep1.vorname} ${currentStep1.nachname}`);
            setApprovalStep1SearchResults([currentStep1]);
        } else {
            setApprovalStep1InputValue('');
            setApprovalStep1SearchResults([]);
        }

        const currentStep2 = mitarbeiter.find((m) => m.personalID === item.userFreigabe2);
        if (currentStep2) {
            setApprovalStep2InputValue(currentStep2.fullName || `${currentStep2.vorname} ${currentStep2.nachname}`);
            setApprovalStep2SearchResults([currentStep2]);
        } else {
            setApprovalStep2InputValue('');
            setApprovalStep2SearchResults([]);
        }
    };

    // Handle edit drawer close
    const handleCloseEditDrawer = () => {
        setIsEditDrawerOpen(false);
        setEditingItem(null);
        setEditFormData({});
        setSaveError(null);
        setApprovalStep1SearchResults([]);
        setApprovalStep2SearchResults([]);
        setApprovalStep1InputValue('');
        setApprovalStep2InputValue('');
    };

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);
            setSaveError(null);

            if (!editingItem) return;

            const updatedItem: HeadOfInstituteItem = {
                ...editingItem,
                ...(editFormData as Partial<HeadOfInstituteItem>),
            };

            await dataService?.updateHeadsOfInstitute([updatedItem]);

            // Refresh the list from the server
            await fetchHeadsOfInstitute();

            handleCloseEditDrawer();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to save';
            setSaveError(errorMessage);
            console.error('Error saving:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Define table columns
    const columns: TableColumnDefinition<HeadOfInstituteItem>[] = [
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'action',
            renderHeaderCell: () => '',
            renderCell: (item) => (
                <Button
                    appearance="subtle"
                    icon={<Edit24Regular />}
                    onClick={() => handleEditItem(item)}
                    title="Edit"
                />
            ),
        }),
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'bezeichnung',
            renderHeaderCell: () => 'Institute',
            renderCell: (item) => item.bezeichnung || '-',
        }),
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'userFreigabe1',
            renderHeaderCell: () => 'Approval Step 1',
            renderCell: (item) => getMitarbeiterName(item.userFreigabe1),
        }),
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'userFreigabe2',
            renderHeaderCell: () => 'Approval Step 2',
            renderCell: (item) => getMitarbeiterName(item.userFreigabe2),
        }),
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'isLocked',
            renderHeaderCell: () => 'Locked',
            renderCell: (item) => (
                <div
                    className={styles.yesNoTag}
                    style={{
                        backgroundColor: item.isLocked ? '#d4edda' : '#f8d7da',
                        color: item.isLocked ? '#155724' : '#721c24',
                    }}
                >
                    {item.isLocked ? 'Yes' : 'No'}
                </div>
            ),
        }),
        createTableColumn<HeadOfInstituteItem>({
            columnId: 'isKpiRelevant',
            renderHeaderCell: () => 'KPI Relevant',
            renderCell: (item) => (
                <div
                    className={styles.yesNoTag}
                    style={{
                        backgroundColor: item.isKpiRelevant ? '#d4edda' : '#f8d7da',
                        color: item.isKpiRelevant ? '#155724' : '#721c24',
                    }}
                >
                    {item.isKpiRelevant ? 'Yes' : 'No'}
                </div>
            ),
        }),
    ];

    return (
        <div className={styles.container}>
            <h3>Head of Institutes Management</h3>

            {isLoading ? (
                <div className={styles.spinnerContainer}>
                    <Spinner label="Loading data..." />
                </div>
            ) : items.length === 0 ? (
                <div className={styles.emptyStateContainer}>
                    <p>No head of institute records found.</p>
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <DataGrid
                        items={items}
                        columns={columns}
                        sortable={false}
                        size="small"
                        aria-label="Head of Institutes"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell, columnId }) => {
                                    const getColumnStyle = () => {
                                        switch (columnId) {
                                            case 'action':
                                                return { width: '50px', minWidth: '50px', maxWidth: '50px' };
                                            case 'bezeichnung':
                                                return { width: '25%', minWidth: '200px' };
                                            case 'userFreigabe1':
                                            case 'userFreigabe2':
                                                return { width: '20%', minWidth: '150px' };
                                            case 'isLocked':
                                            case 'isKpiRelevant':
                                                return { width: '100px', minWidth: '100px' };
                                            default:
                                                return {};
                                        }
                                    };
                                    return (
                                        <DataGridHeaderCell style={getColumnStyle()}>
                                            {renderHeaderCell()}
                                        </DataGridHeaderCell>
                                    );
                                }}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<HeadOfInstituteItem>>
                            {({ item, rowId }) => (
                                <DataGridRow
                                    key={rowId}
                                    onDoubleClick={() => handleEditItem(item)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {({ columnId }) => {
                                        const getColumnStyle = () => {
                                            switch (columnId) {
                                                case 'action':
                                                    return { width: '50px', minWidth: '50px', maxWidth: '50px' };
                                                case 'bezeichnung':
                                                    return { width: '25%', minWidth: '200px' };
                                                case 'userFreigabe1':
                                                case 'userFreigabe2':
                                                    return { width: '20%', minWidth: '150px' };
                                                case 'isLocked':
                                                case 'isKpiRelevant':
                                                    return { width: '100px', minWidth: '100px' };
                                                default:
                                                    return {};
                                            }
                                        };
                                        return (
                                            <DataGridCell style={getColumnStyle()}>
                                                {columns
                                                    .find((col) => col.columnId === columnId)
                                                    ?.renderCell(item as HeadOfInstituteItem)}
                                            </DataGridCell>
                                        );
                                    }}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            )}

            {/* Edit Drawer */}
            <Drawer
                open={isEditDrawerOpen}
                onOpenChange={(_, { open }) => !open && handleCloseEditDrawer()}
                position="end"
                size="medium"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleCloseEditDrawer}
                            />
                        }
                    >
                        Edit Head of Institute
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {/* Institute Input (Editable) */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Institute</label>
                            <Input
                                value={editFormData.bezeichnung || ''}
                                onChange={(_, data) => {
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        bezeichnung: data.value,
                                    }));
                                }}
                                placeholder="Enter institute name..."
                            />
                        </div>

                        {/* Approval Step 1 Combobox */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Approval Step 1</label>
                            <Combobox
                                placeholder="Enter at least 2 characters..."
                                value={approvalStep1InputValue}
                                onOptionSelect={(_, option) => {
                                    const personalID = parseInt(option.optionValue || '0');
                                    const selected = mitarbeiter.find((m) => m.personalID === personalID);
                                    if (selected) {
                                        setApprovalStep1InputValue(
                                            selected.fullName || `${selected.vorname} ${selected.nachname}`
                                        );
                                    }
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        userFreigabe1: personalID || undefined,
                                    }));
                                }}
                                onChange={(ev) => {
                                    const value = (ev.target as HTMLInputElement).value;
                                    setApprovalStep1InputValue(value);
                                    if (value.length === 0) {
                                        setEditFormData((prev) => ({
                                            ...prev,
                                            userFreigabe1: undefined,
                                        }));
                                        setApprovalStep1SearchResults([]);
                                    } else {
                                        handleApprovalStep1Search(value);
                                    }
                                }}
                            >
                                {approvalStep1SearchResults.map((member) => (
                                    <Option
                                        key={member.personalID}
                                        value={member.personalID.toString()}
                                        text={member.fullName || `${member.vorname} ${member.nachname}`}
                                    >
                                        {member.fullName || `${member.vorname} ${member.nachname}`}
                                    </Option>
                                ))}
                            </Combobox>
                        </div>

                        {/* Approval Step 2 Combobox */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Approval Step 2</label>
                            <Combobox
                                placeholder="Enter at least 2 characters..."
                                value={approvalStep2InputValue}
                                onOptionSelect={(_, option) => {
                                    const personalID = parseInt(option.optionValue || '0');
                                    const selected = mitarbeiter.find((m) => m.personalID === personalID);
                                    if (selected) {
                                        setApprovalStep2InputValue(
                                            selected.fullName || `${selected.vorname} ${selected.nachname}`
                                        );
                                    }
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        userFreigabe2: personalID || undefined,
                                    }));
                                }}
                                onChange={(ev) => {
                                    const value = (ev.target as HTMLInputElement).value;
                                    setApprovalStep2InputValue(value);
                                    if (value.length === 0) {
                                        setEditFormData((prev) => ({
                                            ...prev,
                                            userFreigabe2: undefined,
                                        }));
                                        setApprovalStep2SearchResults([]);
                                    } else {
                                        handleApprovalStep2Search(value);
                                    }
                                }}
                            >
                                {approvalStep2SearchResults.map((member) => (
                                    <Option
                                        key={member.personalID}
                                        value={member.personalID.toString()}
                                        text={member.fullName || `${member.vorname} ${member.nachname}`}
                                    >
                                        {member.fullName || `${member.vorname} ${member.nachname}`}
                                    </Option>
                                ))}
                            </Combobox>
                        </div>

                        {/* Locked Checkbox */}
                        <div className={styles.formGroup}>
                            <Checkbox
                                checked={editFormData.isLocked || false}
                                onChange={(_, data) => {
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        isLocked: data.checked === true,
                                    }));
                                }}
                                label="Locked"
                            />
                        </div>

                        {/* KPI Relevant Checkbox */}
                        <div className={styles.formGroup}>
                            <Checkbox
                                checked={editFormData.isKpiRelevant || false}
                                onChange={(_, data) => {
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        isKpiRelevant: data.checked === true,
                                    }));
                                }}
                                label="KPI Relevant"
                            />
                        </div>

                        {saveError && (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: '#fed9cc',
                                    color: '#bc3b0a',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                }}
                            >
                                {saveError}
                            </div>
                        )}

                        <div className={styles.buttonGroup}>
                            <Button
                                appearance="primary"
                                onClick={handleSave}
                                disabled={isSaving || !editFormData.userFreigabe1 || !editFormData.userFreigabe2 || !editFormData.bezeichnung}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button appearance="secondary" onClick={handleCloseEditDrawer} disabled={isSaving}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>
        </div>
    );
};

export default HeadOfInstitutesTab;
